// @ts-nocheck
// Drill-down for one agent on the team board: identity, live state, profile
// login badges, break countdown and today's counters from team-lead-stats.
// z-[1100] on the content is required — Radix portals to <body> at z-50 and
// /cti2's shell sits at z-[1000], so a default dialog opens invisibly behind
// it.
import React from 'react';
import { Coffee, PhoneCall } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { clock } from './teamModel';
import { Dot, HAIRLINE, MiniBar, TONE, nf } from './teamUi';
import { initialsFromName } from '../utils/format';
import SnoopButtons from './SnoopButtons';

const PROFILE_TYPES = [
  { type: 'Mobile', short: 'MOB' },
  { type: 'Soft Phone', short: 'SIP' },
  { type: 'WEBRTC', short: 'WEB' },
];

function ProfileBadges({ agent }) {
  const profiles = agent?.agentTelephony?.profile || [];
  return (
    <div className="flex flex-wrap gap-1.5">
      {PROFILE_TYPES.map(({ type, short }) => {
        const profile = profiles.find((p) => p.type === type);
        const active = Boolean(profile?.active_profile);
        return (
          <span
            key={type}
            title={active ? `Logged in on ${type}` : `Not logged in on ${type}`}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9.5px] font-bold ${
              active ? `${TONE.good.soft} ${TONE.good.text}` : 'bg-muted text-muted-foreground'
            }`}
          >
            <Dot tone={active ? 'good' : 'off'} size={5} />
            {short}
          </span>
        );
      })}
    </div>
  );
}

function Metric({ label, value, tone }) {
  return (
    <div className={`rounded-xl border bg-muted/30 px-3 py-2 ${HAIRLINE}`}>
      <div className="truncate text-[9.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 truncate text-[17px] font-semibold leading-none tabular-nums ${tone ? TONE[tone].text : 'text-foreground'}`}>
        {value}
      </div>
    </div>
  );
}

export default function AgentDetailDialog({ row, onClose, snoop, canSnoop }) {
  const open = Boolean(row);
  const stats = row?.stats;
  const initials = initialsFromName(row?.name, 'A');

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="z-[1100] sm:max-w-lg" overlayClassName="z-[1090]">
        <DialogHeader>
          <DialogTitle className="text-[14px]">Agent detail</DialogTitle>
        </DialogHeader>

        {row ? (
          <div className="space-y-3">
            <div className={`flex items-start gap-3 rounded-2xl border bg-muted/30 p-3 ${HAIRLINE}`}>
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-primary/10 text-[13px] font-bold text-primary">
                {initials}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[14px] font-semibold text-foreground">{row.name}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                  {row.number ? <span className="tabular-nums">#{row.number}</span> : null}
                  {row.extension ? <span className="tabular-nums">x{row.extension}</span> : null}
                  {row.groupName ? <span>{row.groupName}</span> : null}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${TONE[row.state.tone].text}`}>
                    <Dot tone={row.state.tone} size={6} dim={row.state.dim} />
                    {row.state.label}
                  </span>
                  {row.activity ? (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                      {row.activity}
                    </span>
                  ) : null}
                </div>
                <div className="mt-2">
                  <ProfileBadges agent={row.agent} />
                </div>
              </div>
            </div>

            {row.breakInfo ? (
              <div
                className={`flex items-center gap-2 rounded-xl px-3 py-2 text-[12px] font-medium ${
                  row.breakInfo.exceeded ? `${TONE.crit.soft} ${TONE.crit.text}` : `${TONE.warn.soft} ${TONE.warn.text}`
                }`}
              >
                <Coffee size={13} />
                {row.breakInfo.title || 'Break'} —{' '}
                <span className="tabular-nums">
                  {row.breakInfo.exceeded
                    ? `over by ${clock(Math.abs(row.breakInfo.remaining))}`
                    : `${clock(row.breakInfo.remaining)} left`}
                </span>
              </div>
            ) : null}

            {row.snoop.answered ? (
              <div className={`flex items-center justify-between gap-2 rounded-xl border bg-card px-3 py-2 ${HAIRLINE}`}>
                <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-foreground">
                  <PhoneCall size={13} className={TONE.call.text} />
                  On a call now
                </span>
                <SnoopButtons row={row} snoop={snoop} allowed={canSnoop} />
              </div>
            ) : null}

            {stats ? (
              <>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <Metric label="Calls today" value={nf(stats.total_calls)} />
                  <Metric label="Connected" value={nf(stats.connected)} tone="good" />
                  <Metric label="Missed" value={nf(stats.missed)} tone="crit" />
                  <Metric label="Inbound" value={nf(stats.inbound)} tone="call" />
                  {/* Outbound/Declined used to be dropped from this modal
                    * entirely — Outbound lived only as a secondary
                    * breakdown row, and Declined wasn't in the payload at
                    * all (backend never forwarded total_declined_calls).
                    * Same tile treatment as the other call heads now. */}
                  <Metric label="Outbound" value={nf(stats.outbound)} tone="call" />
                  <Metric label="Declined" value={nf(stats.declined)} tone="crit" />
                </div>
                <div className={`space-y-2 rounded-xl border bg-muted/20 p-3 ${HAIRLINE}`}>
                  {[
                    { label: 'Connect rate', value: row.connectPct, suffix: '%', tone: 'good' },
                  ].map((m) => (
                    <div key={m.label} className="flex items-center justify-between gap-3">
                      <span className="text-[11px] text-muted-foreground">{m.label}</span>
                      {m.raw ? (
                        <span className={`text-[12px] font-semibold tabular-nums ${TONE[m.tone].text}`}>
                          {nf(m.value)}
                        </span>
                      ) : (
                        <MiniBar
                          value={m.value || 0}
                          tone={m.tone}
                          width={120}
                          showValue={m.value == null ? '—' : `${m.value}${m.suffix || ''}`}
                        />
                      )}
                    </div>
                  ))}
                  <div className={`flex items-center justify-between gap-3 border-t pt-2 ${HAIRLINE}`}>
                    <span className="text-[11px] text-muted-foreground">Total talk time</span>
                    <span className="text-[12px] font-semibold tabular-nums text-foreground">
                      {stats.total_duration || '—'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] text-muted-foreground">Avg handle time</span>
                    <span className="text-[12px] font-semibold tabular-nums text-foreground">
                      {stats.avg_handle || '—'}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="rounded-xl bg-muted/40 px-3 py-4 text-center text-[11.5px] text-muted-foreground">
                No call counters reported for this agent today.
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
